import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    return { user: null, profile: null, loading: false };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(() => {
    // Start in loading state if there's an auth hash to process (magic link)
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('access_token')) {
      return true;
    }
    return false;
  });

  // Fetch user profile from profiles table
  const fetchProfile = async (userId, userEmail) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      console.log('Profile fetch result:', { data, error });
      
      if (error) {
        console.error('Profile fetch error:', error);
        // If RLS blocks or no profile, return null
        return null;
      }
      return data;
    } catch (err) {
      console.error('Error fetching profile:', err);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        console.log('Initial auth check:', { session, error });
        
        if (session?.user) {
          setUser(session.user);
          const userProfile = await fetchProfile(session.user.id);
          setProfile(userProfile);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 3000);

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth event:', event, 'User:', session?.user?.email);
        
        if (session?.user) {
          console.log('Setting user:', session.user.email);
          setUser(session.user);
          setLoading(false);
          
          // Check if main admin by email first (fast path)
          const isMainAdmin = session.user.email === 'walkerthesavant@gmail.com';
          
          // Set immediate profile to prevent spinning
          setProfile({ 
            id: session.user.id, 
            email: session.user.email, 
            role: isMainAdmin ? 'admin' : (session.user.user_metadata?.role || 'attendee')
          });
          
          // Then try to fetch full profile from database (non-blocking)
          fetchProfile(session.user.id, session.user.email).then(userProfile => {
            if (userProfile) {
              setProfile(userProfile);
              console.log('Profile updated from DB:', { email: session.user.email, role: userProfile.role });
            }
          }).catch(err => {
            console.error('Profile fetch failed:', err);
          });
        } else {
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      clearTimeout(timeout);
      subscription?.unsubscribe();
    };
  }, []);

  // Sign up
  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: window.location.origin
      }
    });
    return { data, error };
  };

  // Sign in
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  };

  // Sign out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setProfile(null);
    }
    return { error };
  };

  // Update profile
  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };
    
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    
    if (!error && data) {
      setProfile(data);
    }
    return { data, error };
  };

  // Check if user is admin
  const isAdmin = profile?.role === 'admin';
  const isChurchAdmin = profile?.role === 'church_admin' || isAdmin;
  const isSpeaker = profile?.role === 'speaker' || isAdmin;

  const value = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    isAdmin,
    isChurchAdmin,
    isSpeaker,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
